class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  // CK-FS: Get current country from URL or other source
  getCurrentCountry() {
    // CK-FS: Debug logging for troubleshooting
    const hostname = window.location.hostname;
    console.log('CK-FS: Detecting country for hostname:', hostname);
    
    // Method 1: Check hostname for country-specific domains
    if (hostname.includes('heydear.de')) {
      console.log('CK-FS: Detected Germany from hostname');
      return 'de';
    }
    if (hostname.includes('heydear.ch')) {
      console.log('CK-FS: Detected Switzerland from hostname');
      return 'ch';
    }
    if (hostname.includes('heydear.fr')) {
      console.log('CK-FS: Detected France from hostname');
      return 'fr';
    }
    if (hostname.includes('heydear.it')) {
      console.log('CK-FS: Detected Italy from hostname');
      return 'it';
    }
    if (hostname.includes('heydear.at')) {
      console.log('CK-FS: Detected Austria from hostname');
      return 'at';
    }
    
    // Method 2: Extract from URL path (e.g., /de/, /fr/, /ch/)
    const pathSegments = window.location.pathname.split('/').filter(segment => segment);
    const potentialCountry = pathSegments[0];
    
    // List of valid country codes for your shop
    const validCountries = ['ch', 'de', 'fr', 'it', 'at'];
    
    if (validCountries.includes(potentialCountry)) {
      console.log('CK-FS: Detected country from path:', potentialCountry);
      return potentialCountry;
    }
    
    // Method 3: Check subdomain (e.g., de.heydear.com)
    const subdomain = hostname.split('.')[0];
    if (validCountries.includes(subdomain)) {
      console.log('CK-FS: Detected country from subdomain:', subdomain);
      return subdomain;
    }
    
    // Method 4: Check for stored preference or default to Germany
    const stored = localStorage.getItem('selectedCountry');
    if (stored) {
      console.log('CK-FS: Using stored country:', stored);
      return stored;
    }
    
    console.log('CK-FS: Using default country: de');
    return 'de'; // CK-FS: Default to Germany
  }

  // CK-FS: Check if a product should be excluded based on country
  shouldExcludeProduct(productUrl) {
    const currentCountry = this.getCurrentCountry();
    
    // Extract country from product URL patterns
    // Adjust these patterns based on your URL structure
    const urlCountryMatch = productUrl.match(/\/([a-z]{2})\//);
    const productCountry = urlCountryMatch ? urlCountryMatch[1] : null;
    
    // If product has a specific country in URL and it doesn't match current country
    if (productCountry && productCountry !== currentCountry) {
      return true;
    }
    
    // Additional checks can be added here based on your URL structure
    // For example, if products from other countries have specific patterns:
    const otherCountryPatterns = [
      /\/products\/[^\/]*-de[\/-]/,  // Products ending with -de
      /\/products\/[^\/]*-fr[\/-]/,  // Products ending with -fr
      /\/products\/[^\/]*-it[\/-]/,  // Products ending with -it
      /\/products\/[^\/]*-ch[\/-]/,  // CK-FS: Added Switzerland pattern
    ];
    
    const currentCountryPattern = new RegExp(`/products/[^/]*-${currentCountry}[/-]`);
    
    // If URL matches other country patterns but not current country
    const matchesOtherCountry = otherCountryPatterns.some(pattern => pattern.test(productUrl));
    const matchesCurrentCountry = currentCountryPattern.test(productUrl);
    
    if (matchesOtherCountry && !matchesCurrentCountry) {
      return true;
    }
    
    return false;
  }

  // CK-FS: Check if a collection should be excluded based on country
  shouldExcludeCollection(collectionUrl) {
    const currentCountry = this.getCurrentCountry();
    
    // Extract country from collection URL patterns
    const urlCountryMatch = collectionUrl.match(/\/([a-z]{2})\//);
    const collectionCountry = urlCountryMatch ? urlCountryMatch[1] : null;
    
    // If collection has a specific country in URL and it doesn't match current country
    if (collectionCountry && collectionCountry !== currentCountry) {
      return true;
    }
    
    // CK-FS: Check for foreign language collection names
    const collectionName = collectionUrl.split('/collections/')[1]?.split('?')[0]?.toLowerCase();
    
    if (collectionName) {
      // Define collection names by language/region
      const languageCollections = {
        // German/Swiss collections
        'german': [
          'geschenke', 'weihnachten', 'geburtstag', 'liebe', 'paar', 
          'hochzeit', 'familie', 'freunde', 'fest', 'valentinstag'
        ],
        
        // French collections (also used in Switzerland)
        'french': [
          'verres', 'saint-valentin', 'noel', 'cadeaux', 'anniversaire', 
          'amour', 'couple', 'mariage', 'famille', 'amis', 'fete', 'cadeau'
        ],
        
        // Italian collections
        'italian': [
          'san-valentino', 'natale', 'regali', 'compleanno', 'amore', 
          'coppia', 'matrimonio', 'famiglia', 'amici', 'festa'
        ],
        
        // Spanish collections
        'spanish': [
          'regalos', 'navidad', 'cumpleanos', 'amor', 'pareja', 
          'boda', 'familia', 'amigos', 'fiesta', 'san-valentin'
        ],
        
        // Swedish collections
        'swedish': [
          'presenter', 'jul', 'fodelsedag', 'karlek', 'par', 
          'brollop', 'familj', 'vanner', 'fest', 'alla-hjartans-dag'
        ],
        
        // Danish collections
        'danish': [
          'gaver', 'jul', 'fodselsdag', 'kaerlighed', 'par', 
          'bryllup', 'familie', 'venner', 'fest', 'valentinsdag'
        ],
        
        // Finnish collections
        'finnish': [
          'lahjat', 'joulu', 'syntymapaiva', 'rakkaus', 'pari', 
          'haat', 'perhe', 'ystavat', 'juhla', 'ysparinpaiva'
        ],
        
        // Polish collections
        'polish': [
          'prezenty', 'boze-narodzenie', 'urodziny', 'milosc', 'para', 
          'slub', 'rodzina', 'przyjaciele', 'swiety', 'walentynki'
        ]
      };
      
      // Define which languages are allowed for each country
      const countryLanguages = {
        'de': ['german'],                           // Germany: only German
        'ch': ['german', 'french'],                 // Switzerland: German and French
        'se': ['swedish'],                          // Sweden: only Swedish
        'dk': ['danish'],                           // Denmark: only Danish
        'fi': ['finnish'],                          // Finland: only Finnish
        'pl': ['polish'],                           // Poland: only Polish
        'es': ['spanish']                           // Spain: only Spanish
      };
      
      const allowedLanguages = countryLanguages[currentCountry] || ['german'];
      
      // Check if collection name matches any forbidden language
      for (const [language, collections] of Object.entries(languageCollections)) {
        if (!allowedLanguages.includes(language) && collections.includes(collectionName)) {
          console.log(`CK-FS: Excluding ${language} collection "${collectionName}" from ${currentCountry} site`);
          return true;
        }
      }
    }
    
    // Additional checks for collection-specific patterns
    const otherCountryPatterns = [
      /\/collections\/[^\/]*-de[\/-]/,
      /\/collections\/[^\/]*-ch[\/-]/,
      /\/collections\/[^\/]*-se[\/-]/,
      /\/collections\/[^\/]*-dk[\/-]/,
      /\/collections\/[^\/]*-fi[\/-]/,
      /\/collections\/[^\/]*-pl[\/-]/,
      /\/collections\/[^\/]*-es[\/-]/,
    ];
    
    const currentCountryPattern = new RegExp(`/collections/[^/]*-${currentCountry}[/-]`);
    
    const matchesOtherCountry = otherCountryPatterns.some(pattern => pattern.test(collectionUrl));
    const matchesCurrentCountry = currentCountryPattern.test(collectionUrl);
    
    if (matchesOtherCountry && !matchesCurrentCountry) {
      return true;
    }
    
    return false;
  }

  // CK-FS: Filter results based on country to exclude products/collections from other countries
  filterResultsByCountry(resultsContainer) {
    const currentCountry = this.getCurrentCountry();
    console.log('CK-FS: Filtering for country:', currentCountry);
    
    // Filter products
    const productItems = resultsContainer.querySelectorAll('#predictive-search-results-products-list li');
    let removedProducts = 0;
    
    console.log('CK-FS: Found', productItems.length, 'product items to check');
    
    productItems.forEach((item, index) => {
      const productLink = item.querySelector('a');
      if (productLink) {
        const href = productLink.href;
        console.log(`CK-FS: Checking product ${index + 1}: ${href}`);
        
        if (this.shouldExcludeProduct(href)) {
          console.log(`CK-FS: Removing product: ${href}`);
          item.remove();
          removedProducts++;
        }
      }
    });

    // Filter collections (in suggestions section)
    const collectionItems = resultsContainer.querySelectorAll('#predictive-search-results-queries-list li');
    let removedCollections = 0;
    
    console.log('CK-FS: Found', collectionItems.length, 'collection items to check');
    
    collectionItems.forEach((item, index) => {
      const collectionLink = item.querySelector('a');
      const href = collectionLink?.href;
      
      if (href) {
        console.log(`CK-FS: Checking collection ${index + 1}: ${href}`);
        
        // Check if this is a collection link (contains /collections/)
        if (href.includes('/collections/') && this.shouldExcludeCollection(href)) {
          console.log(`CK-FS: Removing collection: ${href}`);
          item.remove();
          removedCollections++;
        }
      }
    });

    // Update result counts if needed
    this.updateResultCounts(resultsContainer, removedProducts, removedCollections);
    
    console.log('CK-FS: Filtering complete. Removed:', removedProducts, 'products,', removedCollections, 'collections');
    
    return {
      removedProducts,
      removedCollections
    };
  }

  // CK-FS: Update result counts in live region after filtering
  updateResultCounts(resultsContainer, removedProducts, removedCollections) {
    const liveRegionElement = resultsContainer.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegionElement) {
      const currentText = liveRegionElement.textContent;
      
      // Extract current counts and update them
      const productCountMatch = currentText.match(/(\d+)\s+products?/);
      const suggestionCountMatch = currentText.match(/(\d+)\s+suggestions?/);
      
      if (productCountMatch && removedProducts > 0) {
        const newProductCount = Math.max(0, parseInt(productCountMatch[1]) - removedProducts);
        const updatedText = currentText.replace(productCountMatch[0], `${newProductCount} ${newProductCount === 1 ? 'product' : 'products'}`);
        liveRegionElement.textContent = updatedText;
      }
      
      if (suggestionCountMatch && removedCollections > 0) {
        const newSuggestionCount = Math.max(0, parseInt(suggestionCountMatch[1]) - removedCollections);
        const updatedText = liveRegionElement.textContent.replace(suggestionCountMatch[0], `${newSuggestionCount} ${newSuggestionCount === 1 ? 'suggestion' : 'suggestions'}`);
        liveRegionElement.textContent = updatedText;
      }
    }
  }

  // CK-FS: Modified renderSearchResults method with country filtering
  renderSearchResults(resultsMarkup) {
    // Create temporary container to manipulate results
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = resultsMarkup;
    
    // Filter results based on current country
    this.filterResultsByCountry(tempContainer);
    
    // Render filtered results
    this.predictiveSearchResults.innerHTML = tempContainer.innerHTML;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);